import { useState } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader } from '../../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import type { ApiResult, MarketplacePromptResponse, PublishMarketplacePromptInput } from '../../../lib/api';
import { useAuthSessionStore } from '../../auth/session-store';
import { truncateText } from '../../../utils/text';
import type { UnifiedPrompt } from '../hooks/use-prompt-source';
import { useTeamStore } from '../store/teams';
import { getApiClient } from '../store/cloud-prompts';
import PublishDialog from './PublishDialog';

const CONTENT_PREVIEW_LENGTH = 100;

interface PromptCardProps {
  prompt: UnifiedPrompt;
  onInsertPrompt: (prompt: UnifiedPrompt) => Promise<void>;
  onEditPrompt: (prompt: UnifiedPrompt) => void;
  onDeletePrompt: (prompt: UnifiedPrompt) => void;
}

export default function PromptCard({
  prompt,
  onInsertPrompt,
  onEditPrompt,
  onDeletePrompt,
}: Readonly<PromptCardProps>): JSX.Element {
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated);
  const isPro = useAuthSessionStore((state) => state.subscription?.isPro ?? false);
  const activeWorkspace = useTeamStore((state) => state.activeWorkspace);

  const canPublish = isAuthenticated && isPro && activeWorkspace.type === 'personal';

  const handlePublish = async (
    input: PublishMarketplacePromptInput,
  ): Promise<ApiResult<{ prompt: MarketplacePromptResponse }>> => {
    const apiClient = getApiClient();

    if (!apiClient) {
      return {
        success: false as const,
        error: {
          error: 'API client not configured',
        },
      };
    }

    return apiClient.publishPromptToMarketplace(input);
  };

  return (
    <>
      <Card className="gap-2">
        <CardHeader>
          <h4 className="m-0 text-sm font-bold text-foreground">{prompt.title}</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 min-h-0 px-0"
                aria-label={`Open actions for ${prompt.title}`}
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canPublish ? (
                <DropdownMenuItem onClick={() => setIsPublishDialogOpen(true)}>
                  Publish to Marketplace
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => onEditPrompt(prompt)}>Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeletePrompt(prompt)} className="text-destructive">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <p className="m-0 whitespace-pre-wrap break-words text-[0.8125rem] text-muted-foreground">
          {truncateText(prompt.content, CONTENT_PREVIEW_LENGTH)}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="self-start" onClick={() => void onInsertPrompt(prompt)}>
            Insert
          </Button>
          {canPublish ? (
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => setIsPublishDialogOpen(true)}
            >
              Publish
            </Button>
          ) : null}
        </div>
      </Card>

      <PublishDialog
        open={isPublishDialogOpen}
        prompt={prompt}
        onClose={() => setIsPublishDialogOpen(false)}
        onPublish={handlePublish}
      />
    </>
  );
}
